// Spiegelt trainerstunden_billing_contacts ↔ club_people (gleiche id).

import { getSupabaseClient } from "../supabase/index.js";
import {
  POSTAL_ADDRESS_FIELD_KEYS,
  pickPostalAddressFields,
} from "./postal-address.js";
import { createClubPerson, updateClubPerson } from "./people.js";
import { CLUB_PERSON_CONTACT_SOURCE_BILLING } from "./contact-source.js";

function __asStr(v) {
  return String(v ?? "").trim();
}

function __emailKey(email) {
  const e = __asStr(email).toLowerCase();
  return e || "";
}

/**
 * Trägt Postadresse auf alle club_people mit derselben E-Mail ein (z. B. nativer Kontakt + billing_contact-Mirror).
 *
 * @param {string} anchorId
 * @param {Record<string, unknown>} patch
 * @param {{ excludeId?: string }} [options]
 */
export async function syncPostalFieldsToEmailMatchedClubPeople(
  anchorId,
  patch,
  options = {},
) {
  const anchor = __asStr(anchorId);
  const excludeId = __asStr(options.excludeId);
  if (!anchor) return { ok: true, skipped: true };

  const hasPostal = POSTAL_ADDRESS_FIELD_KEYS.some(
    (k) => patch[k] !== undefined,
  );
  const hasName =
    patch.first_name !== undefined || patch.last_name !== undefined;
  if (!hasPostal && !hasName) return { ok: true, skipped: true };

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    let email = "";
    const { data: anchorPerson, error: anchorPersonErr } = await supabase
      .from("club_people")
      .select("id,email")
      .eq("id", anchor)
      .maybeSingle();
    if (anchorPersonErr) return { ok: false, error: anchorPersonErr };
    email = __emailKey(anchorPerson?.email);

    if (!email) {
      const { data: anchorBilling, error: anchorBillingErr } = await supabase
        .from("trainerstunden_billing_contacts")
        .select("id,email")
        .eq("id", anchor)
        .maybeSingle();
      if (anchorBillingErr) return { ok: false, error: anchorBillingErr };
      email = __emailKey(anchorBilling?.email);
    }
    if (!email) return { ok: true, skipped: true };

    const { data: peers, error: peersErr } = await supabase
      .from("club_people")
      .select("id,email")
      .ilike("email", email);
    if (peersErr) return { ok: false, error: peersErr };

    for (const row of peers || []) {
      const pid = __asStr(row?.id);
      if (!pid || pid === excludeId) continue;
      if (__emailKey(row?.email) !== email) continue;

      const upd = await updateClubPerson(pid, patch, {
        changedVia: "billing_invoice_correction",
      });
      if (!upd?.ok) return { ok: false, error: upd?.error };

      const bcSync = await syncBillingContactPostalFieldsIfExists(pid, patch);
      if (!bcSync?.ok) return { ok: false, error: bcSync?.error };
    }

    return { ok: true };
  } catch (e) {
    return { ok: false, error: e };
  }
}

/**
 * Legt bzw. aktualisiert einen club_people-Datensatz mit id = billing_contact.id (Mirror für Suche/Liste).
 *
 * @param {object} billingRow Felder wie trainerstunden_billing_contacts (mind. id)
 * @returns {Promise<{ ok: boolean, error?: unknown }>}
 */
export async function upsertClubPersonMirrorFromBillingContact(billingRow) {
  const id = __asStr(billingRow?.id);
  if (!id) return { ok: false, error: "missing-id" };

  const patch = {
    tenant_id: billingRow.tenant_id ?? null,
    first_name: billingRow.first_name ?? null,
    last_name: billingRow.last_name ?? null,
    email: billingRow.email ?? null,
    phone: billingRow.phone ?? null,
    notes: billingRow.notes ?? null,
    invoice_recipient_type: "self",
    billing_contact_id: null,
    relation_to_contact: null,
    billing_frequency: null,
    billing_payment_timing: null,
    is_inactive: false,
    inactive_at: null,
    birth_year: null,
    contact_source: CLUB_PERSON_CONTACT_SOURCE_BILLING,
    ...pickPostalAddressFields(billingRow),
  };

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const { data: existing, error: selErr } = await supabase
      .from("club_people")
      .select("id")
      .eq("id", id)
      .maybeSingle();
    if (selErr) return { ok: false, error: selErr };

    if (existing?.id) {
      const upd = await updateClubPerson(id, patch);
      return upd?.ok ? { ok: true } : { ok: false, error: upd?.error };
    }

    const insertPayload = {
      id,
      ...patch,
      created_by: billingRow.created_by ?? null,
    };
    const ins = await createClubPerson(insertPayload, {
      skipNameDuplicateCheck: true,
    });
    return ins?.ok ? { ok: true } : { ok: false, error: ins?.error };
  } catch (e) {
    return { ok: false, error: e };
  }
}

/**
 * trainerstunden_billing_contacts → gleiche Postadresse (wenn Datensatz existiert).
 *
 * @param {string} contactId
 * @param {Record<string, unknown>} patch
 */
export async function syncBillingContactPostalFieldsIfExists(contactId, patch) {
  const id = __asStr(contactId);
  if (!id) return { ok: true, skipped: true };

  const postal = pickPostalAddressFields(patch);
  const namePatch = {};
  if (patch.first_name !== undefined)
    namePatch.first_name = patch.first_name ?? null;
  if (patch.last_name !== undefined)
    namePatch.last_name = patch.last_name ?? null;

  const updatePayload = {
    ...namePatch,
    street: postal.street,
    house_number: postal.house_number,
    postal_code: postal.postal_code,
    city: postal.city,
    country: postal.country,
    updated_at: new Date().toISOString(),
  };
  if (
    !Object.keys(namePatch).length &&
    !POSTAL_ADDRESS_FIELD_KEYS.some((k) => patch[k] !== undefined)
  ) {
    return { ok: true, skipped: true };
  }

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const { data: existing, error: selErr } = await supabase
      .from("trainerstunden_billing_contacts")
      .select("id")
      .eq("id", id)
      .maybeSingle();
    if (selErr) return { ok: false, error: selErr };
    if (!existing?.id) return { ok: true, skipped: true };

    const { error } = await supabase
      .from("trainerstunden_billing_contacts")
      .update(updatePayload)
      .eq("id", id);
    if (error) return { ok: false, error };
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e };
  }
}

/**
 * club_people (Mirror) → trainerstunden_billing_contacts (SSoT Rechnungsempfänger).
 * @param {object} clubRow club_people-Zeile nach Update
 */
export async function syncBillingContactFromClubPersonMirror(clubRow) {
  const row = clubRow && typeof clubRow === "object" ? clubRow : {};
  const id = __asStr(row.id);
  if (
    !id ||
    __asStr(row.contact_source) !== CLUB_PERSON_CONTACT_SOURCE_BILLING
  ) {
    return { ok: true, skipped: true };
  }

  const postal = pickPostalAddressFields(row);
  const patch = {
    first_name: row.first_name ?? null,
    last_name: row.last_name ?? null,
    email: row.email ?? null,
    phone: row.phone ?? null,
    notes: row.notes ?? null,
    street: postal.street,
    house_number: postal.house_number,
    postal_code: postal.postal_code,
    city: postal.city,
    country: postal.country,
    updated_at: new Date().toISOString(),
  };

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const { error } = await supabase
      .from("trainerstunden_billing_contacts")
      .update(patch)
      .eq("id", id);
    if (error) return { ok: false, error };
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e };
  }
}
